'use client';

import {
  NeonCarbon,
  NeonCrude,
  NeonCSCPricing,
  NeonDashboard,
  NeonEnergy,
  NeonFx,
  NeonInsights,
  NeonMetals,
  NeonPortfolio,
  NeonPrecious,
  NeonTrader,
} from '@mds/assets';
import type { AppSwitcherAccessOptions, AppSwitcherItem, AppSwitcherTokenClaims } from './AppSwitcher';

/**
 * Only `dashboard` ships with a real (relative) `href`. Every other entry has no `href`
 * or `onClick`, so `AppListItem` renders it as an inert, non-interactive row until the
 * host supplies a real URL. Use {@link getAppSwitcherConfigUrls} with
 * {@link applyAppSwitcherItemUrls} to resolve real URLs from the same runtime
 * `window.mdsAppSwitcherConfig`/`window._env` config the `mds-app-switcher` web component
 * already reads — no hardcoded URLs needed in the consuming app.
 */
export const defaultAppSwitcherItems: readonly AppSwitcherItem[] = [
  { id: 'carbon', label: 'Carbon', icon: <NeonCarbon /> },
  { id: 'crude', label: 'Crude', icon: <NeonCrude /> },
  { id: 'csc-pricing', label: 'CSC Pricing', icon: <NeonCSCPricing /> },
  { id: 'dashboard', label: 'Dashboard', icon: <NeonDashboard />, href: '/dashboard' },
  { id: 'energy', label: 'Energy', icon: <NeonEnergy /> },
  { id: 'fx', label: 'FX', icon: <NeonFx /> },
  { id: 'insights', label: 'Insights', icon: <NeonInsights /> },
  { id: 'metals', label: 'Metals', icon: <NeonMetals /> },
  { id: 'portfolio', label: 'Portfolio', icon: <NeonPortfolio /> },
  { id: 'precious', label: 'Precious', icon: <NeonPrecious /> },
  { id: 'trader', label: 'Trader', icon: <NeonTrader /> },
];

const defaultAppSwitcherPermissionScopes: Readonly<Record<string, readonly string[]>> = {
  agile: ['agile'],
  carbon: ['carbon'],
  concierge: ['concierge'],
  crude: ['crude'],
  crypto: ['crypto'],
  'csc-pricing': ['CSC Pricing', 'csc-prices:client', 'csc-prices:admin'],
  energy: ['energy'],
  fx: ['FX', 'integral:fx'],
  insights: [
    'admin:insights',
    'insights:content-edit',
    'insights:content-publish',
    'insights:content-reader',
    'insights:content-reader-trial',
  ],
  metals: ['metals'],
  passport: ['passport'],
  payments: ['emporos-client-portal'],
  portfolio: ['portfolio'],
  precious: ['precious', 'integral:precious'],
  risk: ['risk', 'neon-risk'],
  trader: ['trader'],
};

const defaultMobileUnsupportedItemIds = new Set(['energy', 'metals', 'passport']);

/**
 * Normalizes an `appName` value (or a legacy `urls` alias key) into an item `id` — lower-
 * cased, with the one legacy label that doesn't already look like an id (`'CSC Pricing'`)
 * mapped explicitly. Shared by `resolveAppSwitcherItems`'s `appName` matching and the
 * `mds-app-switcher` web component's legacy `app` attribute, so both resolve the same way.
 */
export function normalizeAppSwitcherItemId(value: string) {
  if (value === 'CSC Pricing') {
    return 'csc-pricing';
  }

  return value.toLowerCase();
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function decodeBase64Url(value: string) {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');

  if (typeof globalThis.atob !== 'function') {
    return '';
  }

  const binary = globalThis.atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function getAppSwitcherPermissionsFromToken(accessToken?: string) {
  const payload = accessToken?.split('.')[1];

  if (!payload) {
    return [];
  }

  try {
    return toStringArray((JSON.parse(decodeBase64Url(payload)) as AppSwitcherTokenClaims).permissions);
  } catch {
    return [];
  }
}

function hasXtpGroupClaim(idTokenClaims: AppSwitcherAccessOptions['idTokenClaims'], authDomain?: string) {
  if (!idTokenClaims) {
    return false;
  }

  if (authDomain && idTokenClaims[`https://${authDomain}/xtpGroup`]) {
    return true;
  }

  return Object.entries(idTokenClaims).some(([claim, value]) => claim.endsWith('/xtpGroup') && Boolean(value));
}

export function resolveAppSwitcherItems(
  items: readonly AppSwitcherItem[],
  {
    accessToken,
    appName,
    authDomain,
    disabledItemIds,
    enabledItemIds,
    hiddenItemIds,
    idTokenClaims,
    isMobile,
    hasPermissionContext: hasPermissionContextProp,
    permissions,
  }: AppSwitcherAccessOptions & { hasPermissionContext?: boolean } = {},
) {
  const decodedPermissions = getAppSwitcherPermissionsFromToken(accessToken);
  const permissionSet = new Set([...(permissions ?? []), ...decodedPermissions]);
  const hasPermissionContext =
    hasPermissionContextProp ?? Boolean(accessToken || permissions || enabledItemIds || idTokenClaims);
  const enabledItems = new Set(enabledItemIds);
  const disabledItems = new Set(disabledItemIds);
  const hiddenItems = new Set(hiddenItemIds);
  const currentItemId = appName ? normalizeAppSwitcherItemId(appName) : undefined;

  if (hasXtpGroupClaim(idTokenClaims, authDomain)) {
    enabledItems.add('passport');
  }

  return items
    .filter((item) => item.permitted !== false && !hiddenItems.has(item.id))
    .map((item) => {
      const permissionScopes = item.permissionScopes ?? defaultAppSwitcherPermissionScopes[item.id] ?? [];
      const mobileSupported = item.mobileSupported ?? !defaultMobileUnsupportedItemIds.has(item.id);
      const hasRequiredPermission =
        !hasPermissionContext ||
        permissionScopes.length === 0 ||
        permissionSet.has(item.id) ||
        permissionScopes.some((permission) => permissionSet.has(permission)) ||
        enabledItems.has(item.id);
      const isCurrentApp = currentItemId === item.id;
      // The app rendering the switcher is never a real navigation target — disable it the
      // same way the mds-app-switcher web component's legacy `app` attribute always has,
      // regardless of the permission checks above.
      const disabled = Boolean(
        item.disabled ||
          disabledItems.has(item.id) ||
          (isMobile && !mobileSupported) ||
          !hasRequiredPermission ||
          isCurrentApp,
      );
      const selected = item.selected ?? (isCurrentApp ? true : undefined);

      return { ...item, disabled, selected };
    });
}

export interface AppSwitcherPanelResolvedItem extends AppSwitcherItem {
  disabled: boolean;
  /**
   * True when the user has access to this app, but it isn't supported on the current
   * (mobile) device. Kept out of `disabled` so callers can render it in the accessible
   * tier with a "Desktop only" caption instead of moving it to the no-access tier.
   */
  desktopOnly: boolean;
}

/**
 * Resolves app switcher items for the side-panel and legacy-grid patterns, where apps
 * the user can access but that aren't supported on the current device (`desktopOnly`)
 * must stay in the accessible tier instead of being grouped with genuinely
 * no-access apps (`disabled`).
 */
export function resolveAppSwitcherPanelItems(
  items: readonly AppSwitcherItem[],
  options: Omit<AppSwitcherAccessOptions, 'auth0' | 'authClient' | 'onAuthError'> & {
    hasPermissionContext?: boolean;
  } = {},
): AppSwitcherPanelResolvedItem[] {
  const { isMobile } = options;
  // Resolve permission-based access only; device support is layered on separately below
  // so desktop-only apps can stay in the accessible tier instead of the no-access tier.
  const permissionResolvedItems = resolveAppSwitcherItems(items, { ...options, isMobile: false });

  return permissionResolvedItems.map((resolvedItem) => {
    const originalItem = items.find((item) => item.id === resolvedItem.id)!;
    const mobileSupported = originalItem.mobileSupported ?? !defaultMobileUnsupportedItemIds.has(resolvedItem.id);
    const desktopOnly = Boolean(isMobile && !mobileSupported && !resolvedItem.disabled);

    return { ...resolvedItem, desktopOnly };
  });
}

export type AppSwitcherItemUrls = Partial<Record<string, string>>;

/**
 * Shape of the runtime config every Neon app already provides for the `mds-app-switcher`
 * web component: `window.mdsAppSwitcherConfig`, `window.neonAppSwitcherConfig`, or
 * `window._env` (the `REACT_APP_*` values `neon-app-server` injects into `index.html` at
 * deploy time, or `process.env` locally). Left loose (`[key: string]: unknown`) since each
 * host's actual config object carries many more fields than the app-switcher needs.
 */
export interface AppSwitcherWindowConfig {
  urls?: AppSwitcherItemUrls;
  [key: string]: unknown;
}

const legacyUrlEnvKeysByItemId: Readonly<Record<string, string>> = {
  carbon: 'REACT_APP_CARBON_URL',
  crude: 'REACT_APP_CRUDE_URL',
  'csc-pricing': 'REACT_APP_CSC_PRICING_URL',
  dashboard: 'REACT_APP_BASE_URL',
  energy: 'REACT_APP_ENERGY_URL',
  fx: 'REACT_APP_FX_URL',
  insights: 'REACT_APP_INSIGHTS_URL',
  metals: 'REACT_APP_METALS_URL',
  passport: 'REACT_APP_PASSPORT_URL',
  portfolio: 'REACT_APP_PORTFOLIO_URL',
  precious: 'REACT_APP_PRECIOUS_URL',
  trader: 'REACT_APP_TRADER_URL',
};

const legacyUrlAliasByItemId: Readonly<Record<string, string>> = {
  'csc-pricing': 'CSC Pricing',
  fx: 'FX',
};

function isAppSwitcherWindowConfig(value: unknown): value is AppSwitcherWindowConfig {
  return typeof value === 'object' && value !== null;
}

/**
 * Reads the same runtime app-switcher config every Neon app already sets for the
 * `mds-app-switcher` web component — `window.mdsAppSwitcherConfig`, then
 * `window.neonAppSwitcherConfig`, then `window._env`. Pass the result to
 * {@link getAppSwitcherConfigUrls} so React consumers of `AppSwitcher`/`AppSwitcherPanel`
 * resolve real per-app URLs the same hardcoding-free way the web component does.
 */
export function getAppSwitcherWindowConfig(): AppSwitcherWindowConfig | undefined {
  const globalWindow = (typeof window === 'undefined' ? undefined : window) as
    | { mdsAppSwitcherConfig?: unknown; neonAppSwitcherConfig?: unknown; _env?: unknown }
    | undefined;

  const config = globalWindow?.mdsAppSwitcherConfig ?? globalWindow?.neonAppSwitcherConfig ?? globalWindow?._env;

  return isAppSwitcherWindowConfig(config) ? config : undefined;
}

/**
 * Extracts per-app URLs from an app-switcher window config: `config.urls`, plus the
 * legacy `REACT_APP_<APP>_URL` keys every existing Neon app already sets (e.g.
 * `REACT_APP_INSIGHTS_URL`). Defaults to {@link getAppSwitcherWindowConfig}, so calling it
 * with no arguments resolves URLs from `window` with no per-app hardcoding required:
 *
 * ```tsx
 * const items = applyAppSwitcherItemUrls(defaultAppSwitcherItems, getAppSwitcherConfigUrls() ?? {});
 * ```
 */
export function getAppSwitcherConfigUrls(
  config: AppSwitcherWindowConfig | undefined = getAppSwitcherWindowConfig(),
): AppSwitcherItemUrls | undefined {
  if (!config) {
    return undefined;
  }

  const urls: AppSwitcherItemUrls = { ...config.urls };

  for (const [itemId, alias] of Object.entries(legacyUrlAliasByItemId)) {
    if (urls[itemId] === undefined && typeof urls[alias] === 'string') {
      urls[itemId] = urls[alias];
    }
  }

  for (const [itemId, envKey] of Object.entries(legacyUrlEnvKeysByItemId)) {
    const url = config[envKey];

    if (typeof url === 'string') {
      urls[itemId] = url;
    }
  }

  return Object.keys(urls).length > 0 ? urls : undefined;
}

/**
 * Merges per-app URLs onto app switcher items by `id`. Pass the result as `items` to
 * `AppSwitcher` or `AppSwitcherPanel` so every enabled app renders as a real link instead
 * of an inert row. Pair with {@link getAppSwitcherConfigUrls} to resolve `urls` from
 * `window` instead of hardcoding them.
 *
 * A matching `urls` entry always wins over an item's existing `href` (so a host can
 * override the built-in `dashboard` stub), and items with no matching entry are returned
 * unchanged.
 */
export function applyAppSwitcherItemUrls(
  items: readonly AppSwitcherItem[],
  urls: AppSwitcherItemUrls,
): AppSwitcherItem[] {
  return items.map((item) => {
    const url = urls[item.id];
    return url ? { ...item, href: url } : item;
  });
}
